import { useCallback, useState } from 'react';
import type { UseModalReturn } from '../types';

/**
 * Hook personalizado para manejar el estado y acciones de un modal
 * 
 * Proporciona funcionalidad para abrir/cerrar modales con callback opcional.
 * Optimizado con useCallback para evitar re-renders innecesarios.
 * 
 * @param onCloseModal - Callback opcional ejecutado al cerrar el modal
 * @returns Objeto con estado y funciones para controlar el modal
 * 
 * @example
 * const { isOpen, openModal, closeModal } = useModal(() => {
 *   console.log('Modal cerrado');
 * });
 */
export const useModal = (onCloseModal?: () => void): UseModalReturn => {
    // ============================================================================
    // ESTADO LOCAL
    // ============================================================================

    const [isOpen, setIsOpen] = useState(false);

    // ============================================================================
    // FUNCIONES MEMOIZADAS
    // ============================================================================

    /**
     * Abre el modal
     */
    const openModal = useCallback(() => {
        setIsOpen(true);
    }, []);

    /**
     * Cierra el modal y ejecuta callback opcional
     */
    const closeModal = useCallback(() => {
        setIsOpen(false);
        onCloseModal?.();
    }, [onCloseModal]);

    // ============================================================================
    // RETORNO DEL HOOK
    // ============================================================================

    return {
        isOpen,
        openModal,
        closeModal,
    };
}; 